import React, { Fragment, useCallback } from "react";

import {
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import StatusChipComponent from "components/chip/StatusChip.component";
import { useTranslation } from "react-i18next";
import {
  IconAccessPoint,
  IconRouter,
  IconUsers,
  IconVideoMinus,
  IconWifi,
} from "@tabler/icons";
import { green, orange, red } from "@mui/material/colors";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { getTimeZoneStandard } from "utils/timezone";

const VISITOR = 'visitor'

const StatusSensorRow = ({
  item,
  status,
  onClick = () => { },
  onClickStation = () => { },
  userRole
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const getIconComponent = useCallback(
    (value, status) => {
      const color =
        status === "Critico"
          ? red[500]
          : status === "Attenzione"
            ? orange[500]
            : green[500];

      switch (value) {
        case "GATEWAY":
          return (
            <Tooltip title={t("Gateway")}>
              <IconButton
                size="large"
                sx={{
                  color: color,
                  filter: "drop-shadow(0px 0px 2px rgb(0 0 0 / 0.2))",
                  "&.MuiIconButton-root:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <IconAccessPoint />
              </IconButton>
            </Tooltip>
          );
        case "ENVSENSOR":
          return (
            <Tooltip title={t("environmental monitoring")}>
              <IconButton
                size="large"
                sx={{
                  color: color,
                  filter: "drop-shadow(0px 0px 2px rgb(0 0 0 / 0.2))",
                  "&.MuiIconButton-root:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <IconRouter />
              </IconButton>
            </Tooltip>
          );
        case "CAMERA":
          return (
            <Tooltip title="Camera">
              <IconButton
                size="large"
                sx={{
                  color: color,
                  filter: "drop-shadow(0px 0px 2px rgb(0 0 0 / 0.2))",
                  "&.MuiIconButton-root:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <IconVideoMinus />
              </IconButton>
            </Tooltip>
          );
        case "PCOUNTER":
          return (
            <Tooltip title={t("people-counter")}>
              <IconButton
                size="large"
                sx={{
                  color: color,
                  filter: "drop-shadow(0px 0px 2px rgb(0 0 0 / 0.2))",
                  "&.MuiIconButton-root:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <IconUsers />
              </IconButton>
            </Tooltip>
          );
        case "WIFI":
          return (
            <Tooltip title="WiFi">
              <IconButton
                size="large"
                sx={{
                  color: color,
                  filter: "drop-shadow(0px 0px 2px rgb(0 0 0 / 0.2))",
                  "&.MuiIconButton-root:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <IconWifi />
              </IconButton>
            </Tooltip>
          );

        default:
          return;
      }
    },
    [t]
  );

  return (
    <Fragment>
      <TableRow>
        {userRole !== VISITOR &&
          <TableCell>
            <Tooltip title={t("Monitoring point management")}>
              <IconButton
                color="primary"
                onClick={onClickStation}
                size='small'
                style={{ padding: 0 }}>
                <ManageSearchIcon />
              </IconButton>
            </Tooltip>
          </TableCell>}
        <TableCell align="left">{item?.stationId}</TableCell>
        <TableCell align="left">{item?.address}</TableCell>
        <TableCell align="left">{item?.cluster}</TableCell>

        <TableCell align="left" sx={{ display: "flex", alignItems: "center" }}>
          {item?.envMonitoring &&
            getIconComponent(
              "ENVSENSOR",
              item?.envMonitoringStatus === -1 ||
                item?.envMonitoringStatus === 40
                ? "Critico"
                : item?.envMonitoringStatus === 30
                  ? "Attenzione"
                  : "Ok"
            )}

          {item?.peopleCounter &&
            getIconComponent(
              "PCOUNTER",
              item?.peopleCounterStatus === -1 ||
                item?.peopleCounterStatus === 40
                ? "Critico"
                : item?.peopleCounterStatus === 30
                  ? "Attenzione"
                  : "Ok"
            )}

          {item?.camera &&
            getIconComponent(
              "CAMERA",
              item?.cameraStatus === -1 || item?.cameraStatus === 40
                ? "Critico"
                : item?.cameraStatus === 30
                  ? "Attenzione"
                  : "Ok"
            )}
          {item?.wifi &&
            getIconComponent(
              "WIFI",
              item?.wifiStatus === -1 || item?.wifiStatus === 40
                ? "Critico"
                : item?.wifiStatus === 30
                  ? "Attenzione"
                  : "Ok"
            )}
          {item?.gateway &&
            getIconComponent(
              "GATEWAY",
              item?.gatewayStatus === -1 || item?.gatewayStatus === 40
                ? "Critico"
                : item?.gatewayStatus === 30
                  ? "Attenzione"
                  : "Ok"
            )}
        </TableCell>
        <TableCell align="left">
          {status && (
            <Tooltip
              title={
                item?.statusUpdateTime !== null
                  ? `${t("Updated to")}: ${DateTime.fromISO(
                    getTimeZoneStandard(item?.statusUpdateTime)
                  ).toLocaleString(DateTime.DATETIME_MED, {
                    locale: i18n?.language ? i18n.language : "it",
                  })}`
                  : `${t("Updated to")}: ${t('unknown')}`
              }
            >
              <IconButton>
                <StatusChipComponent status={status} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>


        {userRole !== VISITOR && (
          <TableCell>
            <Tooltip title={t("View Alarms")}>
              <IconButton onClick={onClick}>
                <VisibilityTwoToneIcon
                  style={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                  }}
                />
              </IconButton>
            </Tooltip>
          </TableCell>
        )}

      </TableRow>
    </Fragment>
  );
};

export default StatusSensorRow;
